import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Col, Menu, MenuProps, Row, message } from 'antd';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { useWindowSize } from 'usehooks-ts';

import { logoutApi } from 'apis/auth/logout';
import useAppSelector from 'hooks/useAppSelector';
import { resetUserState } from 'features/userSlice';

import styles from './Gnb.module.css';

type MenuItem = Required<MenuProps>['items'][number];

const APP_NAME = process.env.REACT_APP_NAME;

const Gnb = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { width } = useWindowSize();

  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === '/') {
      navigate('/auctions', { replace: true });
    }
  }, [navigate, pathname]);

  const { marketplaces } = useAppSelector((state) => state.common);
  const { authenticated, loginUser } = useAppSelector((state) => state.user);

  const gotoCreateAuctionPage = () => {
    if (loginUser?.role !== 'seller') {
      message.info('상품 등록을 원하시면 먼저 아래 판매자 등록을 해주세요');
      return;
    }

    navigate('/auctions/new');
  };

  const handleLogout = () => {
    logoutApi().then(() => {
      dispatch(resetUserState());
      navigate('/');
    });
  };

  const handleClickMenu: MenuProps['onClick'] = (info) => {
    if (info.key === 'create_auction') gotoCreateAuctionPage();
    else if (info.key === 'logout') handleLogout();
  };

  /* Sub Menus */
  const marketplaceMenuItems: MenuItem[] = marketplaces.map((marketplace) => ({
    key: `marketplace_${marketplace.id}`,
    label: <Link to={`/auctions?marketplace_id=${marketplace.id}`}>{marketplace.name}</Link>,
  }));

  const sellerMenuItems: MenuItem[] = [];
  if (loginUser?.role === 'seller') {
    sellerMenuItems.push({ key: 'create_auction', label: '상품 등록' });
    sellerMenuItems.push({ key: 'seller_hub', label: <Link to="/seller/auctions">Seller Hub</Link> });
  } else {
    sellerMenuItems.push({
      key: 'register_seller',
      label: (
        <a href={process.env.REACT_APP_SELLER_REGISTER_URL} target="_blank" rel="noreferrer">
          판매자 등록
        </a>
      ),
    });
  }

  const authMenuItems: MenuItem[] = [];
  if (authenticated && loginUser) {
    authMenuItems.push({
      key: 'manage_user_info',
      label: <Link to={`/users/${loginUser.id}`}>내 프로필</Link>,
      icon: <UserOutlined />,
    });
    authMenuItems.push({
      key: 'logout',
      label: '로그아웃',
      icon: <LogoutOutlined />,
    });
  }

  /* Menus */
  const menuItems: MenuItem[] = [
    {
      key: 'home',
      label: (
        <Link to="/">
          <b>{APP_NAME}</b>
        </Link>
      ),
    },
    {
      key: 'marketplace',
      label: '마켓플레이스',
      children: marketplaceMenuItems,
      theme: 'light',
    },
  ];

  if (sellerMenuItems.length)
    menuItems.push({ key: 'seller', label: '상품 판매', children: sellerMenuItems, theme: 'light' });

  if (authMenuItems.length) {
    menuItems.push({
      key: 'user',
      label: (
        <Row align="middle" wrap={false} gutter={4}>
          <Col>
            <UserOutlined />
          </Col>
          {width >= 400 ? <Col>{loginUser?.localized_name}</Col> : null}
        </Row>
      ),
      children: authMenuItems,
      theme: 'light',
    });
  } else {
    menuItems.push({ key: 'login', label: <Link to="/auth/login">로그인</Link> });
  }

  return (
    <Menu
      className={styles.gnb}
      mode="horizontal"
      theme="dark"
      selectable={false}
      items={menuItems}
      onClick={handleClickMenu}
    />
  );
};

export default Gnb;
